"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { db } from "@/lib/instantdb/client"
import { LogoHeader } from "@/components/shared/LogoHeader"
import { GlassCard } from "@/components/shared/GlassCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import Link from "next/link"
import { ArrowRight, Loader2 } from "lucide-react"

const APP_ID = process.env.NEXT_PUBLIC_INSTANTDB_APP_ID || ""
const IS_MOCK_MODE = !APP_ID || APP_ID === "placeholder_app_id" || APP_ID.trim() === ""

const MAGIC_CODE_REQUEST_PATH = "/api/auth/otp/request"

const postMagicRequest = async <T,>(path: string, body: Record<string, any>): Promise<T> => {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }))
    throw new Error(error.message || `Request failed: ${response.statusText}`)
  }

  return response.json()
}

const requestServerMagicCode = (email: string) =>
  postMagicRequest<{ success: boolean; mock?: boolean }>(MAGIC_CODE_REQUEST_PATH, { email })

export const dynamic = "force-dynamic"

export default function SignupPage() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Capture signup metadata
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Store referrer and user agent in sessionStorage for later use
      const referrer = document.referrer || ""
      const userAgent = navigator.userAgent || ""
      
      if (referrer) {
        sessionStorage.setItem("signup-referrer", referrer)
      }
      sessionStorage.setItem("signup-user-agent", userAgent)
      
      // Try to determine source from URL params or referrer
      const urlParams = new URLSearchParams(window.location.search)
      const utmSource = urlParams.get("utm_source") || urlParams.get("source")
      const ref = urlParams.get("ref")
      
      let source = "direct"
      if (utmSource) {
        source = utmSource
      } else if (ref) {
        source = `referral-${ref}`
      } else if (referrer) {
        try {
          const refUrl = new URL(referrer)
          if (refUrl.hostname !== window.location.hostname) {
            source = `external-${refUrl.hostname}`
          }
        } catch {
          // Invalid referrer URL
        }
      }
      
      sessionStorage.setItem("signup-source", source)
      
      console.log("📊 [SIGNUP] Captured metadata:", { source, referrer: referrer || 'none', userAgent: userAgent.substring(0, 50) + '...' })
    }
  }, [])

  // Auto-fill email from sessionStorage if coming from login
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedEmail = sessionStorage.getItem("signup-email")
      if (savedEmail) {
        setEmail(savedEmail)
        sessionStorage.removeItem("signup-email")
      }
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    console.log("🔘 [SIGNUP] Form submitted", { email, acceptedTerms, loading })
    
    setError("")
    
    if (!email.trim()) {
      setError("Email is required")
      return
    }

    if (!acceptedTerms) {
      setError("You must accept the Terms & Conditions")
      return
    }

    if (loading) {
      console.warn("⚠️ [SIGNUP] Already processing, ignoring duplicate submit")
      return
    }

    setLoading(true)
    console.log("📧 [SIGNUP] Starting OTP send process...")

    try {
      const normalizedEmail = email.trim().toLowerCase()
      
      // Store phone in sessionStorage for use in verify
      if (phone.trim()) {
        sessionStorage.setItem("signup-phone", phone.trim())
      }

      // Persist terms acceptance so we don't ask again after verify
      if (typeof window !== "undefined") {
        localStorage.setItem("terms-accepted", "true")
        localStorage.setItem("terms-accepted-date", new Date().toISOString())
      }

      console.log("📧 [SIGNUP] Sending OTP to:", normalizedEmail)

      // Send magic code - try server first, fallback to client
      let result: { code?: string; sent?: true } | { success: boolean }
      if (IS_MOCK_MODE) {
        result = await db.auth.sendMagicCode({ email: normalizedEmail })
      } else {
        try {
          result = await requestServerMagicCode(normalizedEmail)
        } catch (primaryErr) {
          console.warn("⚠️ [SIGNUP] Primary send failed, falling back to client:", primaryErr)
          result = await db.auth.sendMagicCode({ email: normalizedEmail })
        }
      }

      console.log("=".repeat(50))
      console.log(`🔐 Magic code sent to ${normalizedEmail}`)
      console.log(`   Check your email for the 6-digit code`)
      console.log("=".repeat(50))

      // In development, InstantDB may return the code for testing
      const returnedCode = (result as { code?: string }).code
      if (returnedCode) {
        console.log(`🔐 DEV CODE: ${returnedCode}`)
      }

      // Redirect to verify page with email
      router.push(`/verify?email=${encodeURIComponent(normalizedEmail)}`)
    } catch (err: any) {
      console.error("❌ [SIGNUP] Failed to send magic code:", err)
      const errorMessage = err?.message || err?.toString() || "Failed to send verification code. Please try again."
      setError(errorMessage)
      console.error("❌ [SIGNUP] Error details:", {
        message: errorMessage,
        error: err,
        stack: err?.stack
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <LogoHeader />
      <main className="mx-auto flex min-h-[calc(100vh-80px)] max-w-md items-center px-4 py-12">
        <GlassCard className="w-full p-6 md:p-8">
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">Create Free Account</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Get instant access to free resources and exclusive updates.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+44 7700 900000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
                className="mt-1"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={loading}
                className="mt-1 h-4 w-4 rounded border-border"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground">
                I agree to the{" "}
                <Link href="/terms" className="text-accent-teal hover:underline">
                  Terms & Conditions
                </Link>
              </label>
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/20 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  Create Free Account
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link 
              href="/login"
              onClick={(e) => {
                if (email) {
                  sessionStorage.setItem("login-email", email)
                }
              }}
              className="text-accent-teal hover:underline"
            >
              Sign in
            </Link>
          </p>
        </GlassCard>
      </main>
    </div>
  )
}

